import api from './api';

export interface CCPDocument {
  _id: string;
  workspaceId: string;
  projectId: string;
  fileName: string;
  url: string;
  path: string;
  size: number;
  processingStatus: 'pending' | 'processing' | 'completed' | 'failed' | 'unassigned';
  processingError?: string;
  extractedData?: {
    unitNumber?: string;
    unitNumberVariants?: string[];
    rawText?: string;
  };
  dealId?: string;
  fileId?: string;
  manuallyAssignedBy?: string;
  manuallyAssignedAt?: string;
  createdAt: string;
  updatedAt: string;
  processedAt?: string;
}

export interface CCPDocumentsResponse {
  documents: CCPDocument[];
  count: number;
}

export interface UploadCCPDocumentResponse {
  message: string;
  document: CCPDocument;
}

export interface ProcessDocumentResponse {
  message: string;
  document: CCPDocument;
  deal?: {
    _id: string;
    unitNo: string;
    dealNumber: string;
  };
}

export interface BulkProcessResponse {
  message: string;
  results: {
    success: Array<{
      documentId: string;
      fileName: string;
      unitNumber?: string;
      dealId?: string;
    }>;
    unassigned: Array<{
      documentId: string;
      fileName: string;
      unitNumber?: string;
    }>;
    failed: Array<{
      documentId: string;
      fileName?: string;
      error: string;
    }>;
    summary: {
      total: number;
      successCount: number;
      unassignedCount: number;
      failedCount: number;
    };
  };
}

export interface AssignToDealResponse {
  message: string;
  document: CCPDocument;
  deal: {
    _id: string;
    unitNo: string;
    dealNumber: string;
  };
}

export const ccpDocumentService = {
  /**
   * Upload a CCP PDF document with progress tracking
   */
  upload: async (
    workspaceId: string,
    projectId: string,
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<CCPDocument> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('projectId', projectId);

    const response = await api.post<UploadCCPDocumentResponse>(
      `/workspaces/${workspaceId}/ccp-documents`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress
      }
    );

    return response.data.document;
  },

  /**
   * Get all CCP documents for a workspace
   */
  getAll: async (
    workspaceId: string,
    filters?: {
      projectId?: string;
      processingStatus?: string;
      dealId?: string;
    }
  ): Promise<CCPDocument[]> => {
    const params = filters || {};
    const response = await api.get<CCPDocumentsResponse>(
      `/workspaces/${workspaceId}/ccp-documents`,
      { params }
    );
    return response.data.documents;
  },

  /**
   * Get a single CCP document by ID
   */
  getById: async (workspaceId: string, documentId: string): Promise<CCPDocument> => {
    const response = await api.get<{ document: CCPDocument }>(
      `/workspaces/${workspaceId}/ccp-documents/${documentId}`
    );
    return response.data.document;
  },

  /**
   * Process a CCP document with AI to extract unit number and match to deal
   */
  process: async (workspaceId: string, documentId: string): Promise<ProcessDocumentResponse> => {
    const response = await api.post<ProcessDocumentResponse>(
      `/workspaces/${workspaceId}/ccp-documents/${documentId}/process`
    );
    return response.data;
  },

  /**
   * Process multiple CCP documents in bulk
   */
  processBulk: async (workspaceId: string, documentIds: string[]): Promise<BulkProcessResponse> => {
    const response = await api.post<BulkProcessResponse>(
      `/workspaces/${workspaceId}/ccp-documents/bulk-process`,
      { documentIds }
    );
    return response.data;
  },

  /**
   * Manually assign a CCP document to a deal
   */
  assignToDeal: async (
    workspaceId: string,
    documentId: string,
    dealId: string
  ): Promise<AssignToDealResponse> => {
    const response = await api.patch<AssignToDealResponse>(
      `/workspaces/${workspaceId}/ccp-documents/${documentId}/assign`,
      { dealId }
    );
    return response.data;
  },

  /**
   * Delete a CCP document
   */
  delete: async (workspaceId: string, documentId: string): Promise<void> => {
    await api.delete(`/workspaces/${workspaceId}/ccp-documents/${documentId}`);
  }
};
